// Wave 3 T3.2: workspace pulse — the commons' heartbeat on the
// dashboard. Four derived lists (7/14-day windows), links into decks.
import { useEffect, useState } from 'react';

interface Pulse {
  top_read: Array<{ deck_id: string; title: string | null; slide_num: number; views: number }>;
  top_referenced: Array<{ deck_id: string; title: string | null; slide_num: number; refs: number }>;
  recent_forks: Array<{ id: string; title: string; source_title: string | null; created_at: number }>;
  recent_decks: Array<{ id: string; title: string; created_at: number }>;
}

export default function WorkspacePulse({
  workspaceId,
  onSelectDeck,
}: {
  workspaceId: string;
  onSelectDeck: (id: string) => void;
}) {
  const [pulse, setPulse] = useState<Pulse | null>(null);

  useEffect(() => {
    let cancelled = false;
    setPulse(null);
    fetch(`/api/workspaces/${workspaceId}/pulse`, { credentials: 'include' })
      .then(r => (r.ok ? r.json() : null))
      .then(d => { if (!cancelled) setPulse(d); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [workspaceId]);

  if (!pulse) return null;
  const empty =
    pulse.top_read.length === 0 && pulse.top_referenced.length === 0 &&
    pulse.recent_forks.length === 0 && pulse.recent_decks.length === 0;
  if (empty) return <p style={{fontSize:12,color:'var(--text-muted)'}}>No recent activity yet.</p>;

  const deckLink = (id: string, label: string) => (
    <button className="editorial-text-action" onClick={() => onSelectDeck(id)} style={{color:"var(--brand)",cursor:"pointer"}}>{label}</button>
  );
  const col = (title: string, rows: React.ReactNode[]) =>
    rows.length > 0 && (
      <div style={{ flex: 1, minWidth: 180 }}>
        <div style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-muted)', marginBottom: 6 }}>{title}</div>
        {rows}
      </div>
    );

  return (
    <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', padding: '14px 16px', margin: '0 0 18px', border: '1px solid var(--border, var(--ds-surface-subtle))', borderRadius: 10, fontSize: 12 }}>
      {col('Most read · 7d', pulse.top_read.map((r, i) => (
        <div key={i} style={{ padding: '2px 0' }}>👁 {r.views} · {deckLink(r.deck_id, r.title ?? r.deck_id)} <span style={{ color: 'var(--text-muted)' }}>p{r.slide_num}</span></div>
      )))}
      {col('Most referenced · 7d', pulse.top_referenced.map((r, i) => (
        <div key={i} style={{ padding: '2px 0' }}>↩ {r.refs} · {deckLink(r.deck_id, r.title ?? r.deck_id)} <span style={{ color: 'var(--text-muted)' }}>p{r.slide_num}</span></div>
      )))}
      {col('Recent forks · 14d', pulse.recent_forks.map(f => (
        <div key={f.id} style={{ padding: '2px 0' }}>⑂ {deckLink(f.id, f.title)}{f.source_title && <span style={{ color: 'var(--text-muted)' }}> ← {f.source_title}</span>}</div>
      )))}
      {col('New decks · 14d', pulse.recent_decks.map(d => (
        <div key={d.id} style={{ padding: '2px 0' }}>＋ {deckLink(d.id, d.title)}</div>
      )))}
    </div>
  );
}
